import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Translate, useTranslate } from 'core/translate';
import { Accordion, AccordionContent, AccordionGroup } from 'core/controls';
import MathBoxAccordionHeader from './MathBoxAccordionHeader';
import SubgroupHeader from './SubgroupHeader';
import OptionList from './OptionList';
import PackagePricing from '../components/packages/PackagePricing';

const computeColorOptions = config => {
  const colors = config.colors;
  return []
    .concat(
      _.flatMap(colors.exterior, 'items'),
      _.flatMap(colors.interior, 'items'),
      colors.stripe,
      colors.top
    )
    .filter(x => x.selected);
};

const computeInteriorOptions = config =>
  _.flatMap(config.interior)
    .filter(x => x.selected)
    .filter(x => !x.includedInPackage);

const computeExteriorOptions = config =>
  []
    .concat(config.engines, _.flatMap(config.exterior))
    .filter(x => x.selected)
    .filter(x => !x.includedInPackage);

const computePackages = config =>
  _.flatMap(config.packages, x => x.options).filter(x => x.selected);

const SummaryPackageOptionsList = ({ options }) => {
  if (!options || options.length === 0) {
    return null;
  }

  return (
    <div className="package-list-item__options">
      <div className="package-list-item__subheader">
        <Translate value="package.includes" />
      </div>
      <OptionList options={options} />
    </div>
  );
};

const SummaryPackagesList = ({ packages }) => {
  const packageListItems = _.sortBy(packages, 'opdMsrp').map(p => (
    <div key={p.id} className="option package-list-item">
      <div className="option__row">
        <div className="option__column package-list-item__content">
          <div
            className="option__title"
            dangerouslySetInnerHTML={{ __html: p.description }}
          />
          <div className="package-list-item__pricing">
            <PackagePricing option={p} />
          </div>
          <SummaryPackageOptionsList options={p.packageOptions} />
        </div>
      </div>
    </div>
  ));

  return <div className="package-list">{packageListItems}</div>;
};

const computePackagesTotal = (packages, translate) => {
  const isPackagePriceActual = _.some(
    packages,
    ({ priceState }) => priceState === 'Unknown'
  );
  if (isPackagePriceActual) {
    return translate('unknown.price.text');
  }
  return packages.reduce((total, { numericMsrp }) => total + numericMsrp, 0);
};

const MathBoxOptions = props => {
  const translate = useTranslate();
  const { pricing, config, isOpenDefault } = props;

  const colors = computeColorOptions(config);
  const colorsTotal = colors.reduce((total, option) => total + option.msrp, 0);
  const colorsHeader = translate('component.common.selected.colors');

  const packages = computePackages(config);
  const packagesHeader = translate('component.common.selected.packages');
  const packagesTotal = computePackagesTotal(packages, translate);

  const interiorOptions = computeInteriorOptions(config);
  const exteriorOptions = computeExteriorOptions(config);
  const options = [...exteriorOptions, ...interiorOptions];
  const optionsTotal = pricing.isSeeDealer
    ? translate('unknown.price.text')
    : options.reduce((total, option) => total + option.msrp, 0);
  const optionsHeader = translate('component.common.selected.options');

  return (
    <div className="math-box__options">
      <Accordion>
        <AccordionGroup isOpenDefault={isOpenDefault} id="selected-colors">
          <MathBoxAccordionHeader header={colorsHeader} total={colorsTotal} />
          <AccordionContent>
            <SubgroupHeader to="/color" analytics={colorsHeader} />
            <OptionList options={colors} />
          </AccordionContent>
        </AccordionGroup>
        {packages.length > 0 && (
          <AccordionGroup isOpenDefault={isOpenDefault} id="selected-packages">
            <MathBoxAccordionHeader
              header={`${packagesHeader} (${packages.length})`}
              total={packagesTotal}
            />
            <AccordionContent>
              <SubgroupHeader to="/packages" analytics={packagesHeader} />
              <SummaryPackagesList packages={packages} />
            </AccordionContent>
          </AccordionGroup>
        )}
        {options.length > 0 && (
          <AccordionGroup isOpenDefault={isOpenDefault} id="selected-options">
            <MathBoxAccordionHeader
              header={`${optionsHeader} (${options.length})`}
              total={optionsTotal}
            />
            <AccordionContent>
              <SubgroupHeader
                title={translate('exterior')}
                to="/exterior"
                analytics={optionsHeader}
              />
              <OptionList options={_.sortBy(exteriorOptions, 'id')} />
              <SubgroupHeader
                title={translate('interior')}
                to="/interior"
                analytics={optionsHeader}
              />
              <OptionList options={_.sortBy(interiorOptions, 'id')} />
            </AccordionContent>
          </AccordionGroup>
        )}
      </Accordion>
    </div>
  );
};

MathBoxOptions.defaultProps = {
  isOpenDefault: false
};

MathBoxOptions.propTypes = {
  isOpenDefault: PropTypes.bool,
  config: PropTypes.object.isRequired,
  pricing: PropTypes.object.isRequired
};

export default MathBoxOptions;
